import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BreadcrumbPageHeader, ListPageBody, ListPageLayout } from '@/shared/ui/layout';
import { PageTransition } from '@/shared/ui/animations';
import {
  AudiencesContentV2,
  CreatePersonaSheet,
  EditPersonaSheet,
  usePersonaFormOptions,
} from '@/features/audiences';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import type { PersonaDto } from '@/lib/api/personas-client';
import type { AudiencesV2Props } from './AudiencesV2.types';

/**
 * Redesigned audiences list. The header is the breadcrumb alone: the prototype
 * carries no page title, description or info button here, and there is no
 * "about" copy for this page to show.
 *
 * Create and edit are sheets; deleting keeps its confirmation dialog, which is
 * what a destructive, irreversible action should interrupt with.
 */
export const AudiencesV2 = ({
  personas,
  isPending,
  isError,
  fetchError,
  refetch,
  onPersonaClick,
  createPersona,
  updatePersona,
  deletePersona,
  isDeleting,
  onViewPersona,
}: AudiencesV2Props) => {
  const { t } = useTranslation('audiences');
  const { t: tCommon } = useTranslation('common');
  const { canManageAudiences } = usePermissions();
  // The create form's segment and topic pickers offer what the workspace
  // already uses; without this they open empty.
  const formOptions = usePersonaFormOptions(personas);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<PersonaDto | null>(null);
  const [deletingPersona, setDeletingPersona] = useState<PersonaDto | null>(null);

  const handleConfirmDelete = () => {
    if (!deletingPersona) return;
    void deletePersona({ id: deletingPersona.id }).then(() => {
      setDeletingPersona(null);
      refetch();
    });
  };

  const deletingName = deletingPersona?.demographicsData?.data
    ? [
        deletingPersona.demographicsData.data.firstName,
        deletingPersona.demographicsData.data.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    : undefined;

  return (
    <ListPageLayout>
      <PageTransition variant="slideUp" delay={0.0}>
        <BreadcrumbPageHeader
          trail={[{ label: t('audiences.audiencesV2.knowledge'), href: ROUTES.KNOWLEDGE_HUB }]}
          page={t('audiences.audiences')}
        />
      </PageTransition>

      <ListPageBody>
        <PageTransition variant="slideUp" delay={0.1} className="flex min-h-0 flex-1 flex-col">
          <AudiencesContentV2
            personas={personas}
            isPending={isPending}
            isError={isError}
            fetchError={fetchError}
            refetch={refetch}
            onPersonaClick={onPersonaClick}
            onCreateClick={canManageAudiences ? () => setIsCreateOpen(true) : undefined}
            onEditPersona={canManageAudiences ? setEditingPersona : undefined}
            onDeletePersona={canManageAudiences ? setDeletingPersona : undefined}
          />
        </PageTransition>
      </ListPageBody>

      {/* Mounted only while open: each sheet seeds its form once, so unmounting
          is what resets it. */}
      {isCreateOpen && (
        <CreatePersonaSheet
          config={formOptions}
          createPersona={createPersona}
          onClose={() => setIsCreateOpen(false)}
          onSuccess={refetch}
          onViewPersona={onViewPersona}
        />
      )}

      {editingPersona && (
        <EditPersonaSheet
          persona={editingPersona}
          updatePersona={updatePersona}
          onClose={() => setEditingPersona(null)}
          onSuccess={refetch}
        />
      )}

      {deletingPersona && (
        <DeleteConfirmationModal
          open
          isDeleting={isDeleting}
          onCancel={() => setDeletingPersona(null)}
          onConfirmDelete={handleConfirmDelete}
          resourceType={tCommon('artifact.artifactKindPalette.persona')}
          resourceName={deletingName}
          warningMessage={t('audiences.audiencesContent.thisActionCannotBeUndone')}
        />
      )}
    </ListPageLayout>
  );
};

AudiencesV2.displayName = 'AudiencesV2';
